import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "editPanel",
    "imagesPanel",
    "previewPanel",
    "editTab",
    "imagesTab",
    "previewTab",
    "tiptap",
    "titleInput",
    "descriptionInput",
    "previewTitle",
    "previewDescription",
    "previewDescriptionWrap",
    "previewHeading",
    "previewBody",
    "heroFrame",
    "previewHero",
    "publishedInput",
    "insertedImagesList"
  ]

  connect() {
    this.onPopState = () => this.syncFromLocation({ push: false })
    window.addEventListener("popstate", this.onPopState)
    queueMicrotask(() => {
      this.rememberSaved()
      this.syncFromLocation({ push: false })
    })
  }

  disconnect() {
    window.removeEventListener("popstate", this.onPopState)
  }

  syncInlineAlt(event) {
    this.withInlineImage(event, (tiptap, signedId) => {
      tiptap.setImageAlt(signedId, event.detail.alt ?? "")
    })
  }

  syncInlineCaption(event) {
    this.withInlineImage(event, (tiptap, signedId) => {
      tiptap.setImageCaption(signedId, event.detail.caption ?? "")
    })
  }

  withInlineImage(event, callback) {
    const signedId = event.detail?.signedId
    if (!signedId || !this.hasTiptapTarget) return

    const tiptap = this.tiptapController()
    if (tiptap) callback(tiptap, signedId)
  }

  removeInlineImage(event) {
    const signedId = event.detail?.signedId
    if (!signedId || !this.hasTiptapTarget) return

    this.tiptapController()?.removeImage(signedId)
  }

  appendInsertedImageCard(event) {
    const html = event.detail?.html
    if (!html || !this.hasInsertedImagesListTarget) return

    const parsed = new DOMParser().parseFromString(html, "text/html")
    this.insertedImagesListTarget.append(...parsed.body.childNodes)
  }

  removeInsertedImageCard(event) {
    const signedId = event.detail?.signedId
    if (!signedId || !this.hasInsertedImagesListTarget) return

    this.insertedImagesListTarget
      .querySelector(`[data-blob-signed-id="${CSS.escape(signedId)}"]`)
      ?.remove()
  }

  showEdit() {
    this.showPanel("edit")
  }

  showImages() {
    this.saveIfNeeded(() => this.showPanel("images"))
  }

  showPreview() {
    this.saveIfNeeded(() => this.renderPreview())
  }

  syncFromLocation({ push = false } = {}) {
    if (!this.hasClientTabs()) return

    const name = this.normalizedTab(this.tabFromLocation())
    if (name === "preview") this.renderPreview({ push })
    else this.showPanel(name, { push })
  }

  hasClientTabs() {
    return this.hasEditPanelTarget &&
      this.hasImagesPanelTarget &&
      this.hasPreviewPanelTarget &&
      this.hasEditTabTarget &&
      this.hasImagesTabTarget &&
      this.hasPreviewTabTarget
  }

  normalizedTab(raw) {
    return raw === "images" || raw === "preview" ? raw : "edit"
  }

  saveIfNeeded(afterSave) {
    if (!this.needsSave()) {
      afterSave()
      return
    }
    this.saveThen(afterSave)
  }

  saveThen(afterSave, afterFail) {
    const form = this.editorForm()
    if (!form || this.savePending) {
      if (!this.savePending && !form) {
        if (afterFail) afterFail()
        else afterSave()
      }
      return
    }

    this.savePending = true
    const finish = () => {
      this.savePending = false
    }
    const onEnd = (event) => {
      if (event.target !== form) return
      cleanup()
      finish()
      if (event.detail?.success) {
        this.rememberSaved()
        afterSave()
      } else afterFail?.()
    }
    const onInvalid = () => {
      cleanup()
      finish()
      afterFail?.()
    }
    const cleanup = () => {
      document.removeEventListener("turbo:submit-end", onEnd)
      form.removeEventListener("invalid", onInvalid, true)
    }

    document.addEventListener("turbo:submit-end", onEnd)
    form.addEventListener("invalid", onInvalid, true)
    form.requestSubmit()
  }

  renderPreview({ push = true } = {}) {
    this.previewTitleTarget.textContent = this.titleInputTarget.value
    const description = this.descriptionInputTarget.value.trim()
    this.previewDescriptionTarget.textContent = description
    this.previewDescriptionWrapTarget.classList.toggle("hidden", description.length === 0)
    this.fillBody()
    this.copyHero()
    this.showPanel("preview", { push })
  }

  editorForm() {
    if (!this.hasTiptapTarget) return null
    const tiptap = this.application.getControllerForElementAndIdentifier(this.tiptapTarget, "tiptap-editor")
    if (!tiptap?.hasFormTarget) return null
    return tiptap.formTarget
  }

  showPanel(name, { push = true } = {}) {
    if (!this.hasClientTabs()) return

    this.editPanelTarget.classList.toggle("hidden", name !== "edit")
    this.imagesPanelTarget.classList.toggle("hidden", name !== "images")
    this.previewPanelTarget.classList.toggle("hidden", name !== "preview")
    this.markTab(this.editTabTarget, name === "edit")
    this.markTab(this.imagesTabTarget, name === "images")
    this.markTab(this.previewTabTarget, name === "preview")
    this.writeTab(name, { push })
  }

  tabFromLocation() {
    return new URLSearchParams(window.location.search).get("tab")
  }

  writeTab(name, { push = true } = {}) {
    const url = new URL(window.location.href)
    if (name === "edit") url.searchParams.delete("tab")
    else url.searchParams.set("tab", name)

    const next = `${url.pathname}${url.search}${url.hash}`
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (next === current) return

    if (push) window.history.pushState(window.history.state, "", url)
    else window.history.replaceState(window.history.state, "", url)
  }

  fillBody() {
    const html = this.tiptapController()?.editor?.getHTML() || ""
    const parsed = new DOMParser().parseFromString(html, "text/html")
    this.previewBodyTarget.replaceChildren(...parsed.body.childNodes)
  }

  copyHero() {
    this.previewHeroTarget.replaceChildren()
    const uploadRoot = this.heroFrameTarget.querySelector("[data-controller~='image-upload']")
    const upload = uploadRoot
      ? this.application.getControllerForElementAndIdentifier(uploadRoot, "image-upload")
      : null
    const source = upload?.previewImageElement()
    const src = source?.getAttribute("src")
    if (!source || !src) {
      this.previewHeroTarget.classList.add("hidden")
      return
    }

    const img = document.createElement("img")
    img.src = source.currentSrc || src
    img.alt = source.alt
    img.className = "h-full w-full object-cover"
    this.previewHeroTarget.appendChild(img)
    this.previewHeroTarget.classList.remove("hidden")
  }

  markTab(tab, active) {
    tab.classList.toggle("border-(--brand-orange)", active)
    tab.classList.toggle("text-(--brand-orange)", active)
    tab.classList.toggle("border-transparent", !active)
    tab.classList.toggle("text-gray-500", !active)
    tab.classList.toggle("hover:border-gray-300", !active)
    tab.classList.toggle("hover:text-gray-800", !active)
    tab.setAttribute("aria-selected", active ? "true" : "false")
  }

  rememberSaved() {
    const state = this.currentState()
    if (state) this.saved = state
  }

  rememberSavedBody(html) {
    if (!this.saved) this.rememberSaved()
    if (this.saved) this.saved.body = html
  }

  needsSave() {
    const saved = this.saved
    const current = this.currentState()
    if (!saved || !current) return true
    return saved.title !== current.title ||
      saved.description !== current.description ||
      saved.published !== current.published ||
      saved.body !== current.body
  }

  currentState() {
    const tiptap = this.tiptapController()
    if (!tiptap?.editor) return null
    return {
      title: this.hasTitleInputTarget ? this.titleInputTarget.value : "",
      description: this.hasDescriptionInputTarget ? this.descriptionInputTarget.value : "",
      published: this.hasPublishedInputTarget ? this.publishedInputTarget.value : "",
      body: tiptap.editor.getHTML()
    }
  }

  tiptapController() {
    if (!this.hasTiptapTarget) return null
    return this.application.getControllerForElementAndIdentifier(this.tiptapTarget, "tiptap-editor")
  }
}
